import { useCallback } from 'react';
import { getBookInfo, formatReference } from '../utils/bibleData';

/**
 * Custom hook for audio processing and file generation
 * Handles audio file generation, verse references, and passage formatting
 */
export const useAudioProcessing = ({
  verseAudioURLs,
  selectedBook,
  selectedChapter,
  startVerse,
  endVerse
}) => {
  // Helper function to get verse reference (e.g., "Genesis 1:30")
  const getVerseReference = useCallback((verseNumber, bookId = 'genesis', chapter = 1) => {
    const book = getBookInfo(bookId);
    return formatReference(book?.name || 'Unknown', chapter, verseNumber);
  }, []);

  // Helper function to get passage string
  const getPassageString = useCallback((book, chapter, startVerse, endVerse) => {
    const bookInfo = getBookInfo(book);
    const bookName = bookInfo?.name || 'Unknown';
    
    if (startVerse === endVerse) {
      return `${bookName} ${chapter}:${startVerse}`;
    } else {
      return `${bookName} ${chapter}:${startVerse}-${endVerse}`;
    }
  }, []);

  // Generate verse audio files for display in modal
  const generateVerseAudioFiles = useCallback(async () => {
    console.log('🎵 Displaying saved verse files...');
    console.log('🎵 Current verseAudioURLs:', verseAudioURLs);
    
    const verseNumbers = Object.keys(verseAudioURLs).map(k => k.replace('verse-', '')).sort((a, b) => a - b);
    const verseReferences = verseNumbers.map(v => getVerseReference(parseInt(v), selectedBook, selectedChapter));
    
    console.log(`📊 REVIEW MODAL: Total verses recorded: ${Object.keys(verseAudioURLs).length}`);
    console.log(`📊 REVIEW MODAL: Verse numbers: ${verseNumbers.join(', ')}`);
    console.log(`📊 REVIEW MODAL: Verse references: ${verseReferences.join(', ')}`);
    
    // No need to generate anything - just show what we have
  }, [verseAudioURLs, selectedBook, selectedChapter, getVerseReference]);

  // Get verse count for display
  const getVerseCount = useCallback(() => {
    return Object.keys(verseAudioURLs).length;
  }, [verseAudioURLs]);

  // Get sorted verse data for display
  const getSortedVerseData = useCallback(() => {
    return Object.values(verseAudioURLs)
      .sort((a, b) => a.verseNumber - b.verseNumber);
  }, [verseAudioURLs]);

  // Get verse numbers as array
  const getVerseNumbers = useCallback(() => {
    return Object.keys(verseAudioURLs).map(k => k.replace('verse-', '')).sort((a, b) => a - b);
  }, [verseAudioURLs]);

  // Get verse references as array
  const getVerseReferences = useCallback(() => {
    const verseNumbers = getVerseNumbers();
    return verseNumbers.map(v => getVerseReference(parseInt(v), selectedBook, selectedChapter));
  }, [getVerseNumbers, selectedBook, selectedChapter, getVerseReference]);

  return {
    // Helper functions
    getVerseReference,
    getPassageString,
    
    // Audio processing functions
    generateVerseAudioFiles,
    
    // Data getters
    getVerseCount,
    getSortedVerseData,
    getVerseNumbers,
    getVerseReferences
  };
};
